import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Layout, Form, Input, Collapse, Switch, Tooltip, Button, Modal } from "antd";
import type EditorJS from "@editorjs/editorjs";
import styled from "styled-components";
import { currentPostAtom } from "../atom";
import { Editor } from "../component/write";
import { FreeTextPostForm, hasFreeTextContent, convertBlocksToFreeText } from "../component/freetext";
import {
  getPostData,
  postPost,
  putPost,
  postPostTable,
  postUpdatePostTable,
  ContentFormat,
  PostContentOptions,
} from "../api/postAPI";

interface EditPostData {
  id: any;
  title: string;
  content: string;
  contentFormat?: ContentFormat;
  contentEn?: string | null;
}

interface PostState {
  postData: EditPostData;
}

interface ConferenceState {
  conferenceMode: true;
  rootCategoryId: number;
  conferenceEditMode?: boolean;
  postData?: EditPostData;
  conferenceInitialValues?: {
    meetKppNumber: string;
    meetGender: string;
    meetAge: string;
    meetOrgan: string;
    meetDiagnosis: string;
    meetOrganization: string;
  };
}

const Write = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPost, setCurrentPost] = useRecoilState(currentPostAtom);
  const state = location.state as PostState | ConferenceState | null;
  const isConferenceMode = state && "conferenceMode" in state && state.conferenceMode;
  const conferenceState = isConferenceMode ? (state as ConferenceState) : null;
  const postState = state && "postData" in state && !isConferenceMode ? (state as PostState) : null;
  const editPostData = postState?.postData ?? conferenceState?.postData;
  const postId = editPostData?.id ?? currentPost.id;
  const [title, setTitle] = useState<string>(editPostData?.title ?? "");
  const [conferenceForm] = Form.useForm();

  // ── 본문 입력 방식 (블록 / FreeText) ──
  // 내용이 이미 저장된 글은 스위치로 형식을 바꾸지 않는다.
  // 블록 글은 "FreeText로 전환" 으로 내용을 변환한 뒤에만 FreeText 로 바꿀 수 있다 (FreeText → 블록은 지원하지 않음).
  const savedFormat: ContentFormat = editPostData?.contentFormat ?? "BLOCK";
  const formatLocked = !!editPostData && hasSavedContent(savedFormat, editPostData.content);
  const [contentFormat, setContentFormat] = useState<ContentFormat>(savedFormat);
  const [englishEnabled, setEnglishEnabled] = useState<boolean>(hasFreeTextContent(editPostData?.contentEn));
  // 두 편집기를 한 번 연 뒤에는 계속 붙여 두어 방식을 바꿔도 작성 중인 내용이 남게 한다
  const [freeTextMounted, setFreeTextMounted] = useState<boolean>(savedFormat === "FREETEXT");
  const isFreeText = contentFormat === "FREETEXT";

  // 블록 → FreeText 전환 결과. 저장 전에는 스위치를 꺼서 블록 방식으로 되돌릴 수 있다.
  const blockEditorRef = useRef<EditorJS | null>(null);
  const [convertedHtml, setConvertedHtml] = useState<string | null>(null);
  // 다시 전환하면 FreeText 편집기를 새 변환 결과로 다시 연다
  const [freeTextKey, setFreeTextKey] = useState(0);
  const [modal, modalContextHolder] = Modal.useModal();
  const canConvert = formatLocked && savedFormat === "BLOCK";
  const switchLocked = formatLocked && convertedHtml === null;

  const changeFreeText = (checked: boolean) => {
    setContentFormat(checked ? "FREETEXT" : "BLOCK");
    if (checked) setFreeTextMounted(true);
  };

  const convertToFreeText = async () => {
    // 저장된 내용이 아니라 지금 블록 편집기에 있는 내용을 변환한다
    let blockContent = editPostData?.content;
    try {
      const current = await blockEditorRef.current?.save();
      if (current) blockContent = JSON.stringify(current);
    } catch (e) {
      console.error("블록 내용 읽기 실패", e);
    }
    const { html, skippedTypes } = convertBlocksToFreeText(blockContent);

    const confirmed = await modal.confirm({
      title: "FreeText로 전환할까요?",
      width: 480,
      content: (
        <ConvertNotice>
          <li>블록 내용을 FreeText로 옮깁니다. 글자 크기, 간격 등 모양이 조금 달라질 수 있습니다.</li>
          <li>Save를 눌러야 반영됩니다. 저장 전에는 "FreeText로 입력하기"를 꺼서 블록 방식으로 되돌릴 수 있습니다.</li>
          <li>저장한 뒤에는 블록 방식으로 되돌릴 수 없습니다.</li>
          {convertedHtml !== null && (
            <li className="warning">앞서 전환한 뒤 FreeText에서 고친 내용은 지워지고, 지금 블록 내용으로 다시 옮깁니다.</li>
          )}
          {skippedTypes.length > 0 && (
            <li className="warning">옮기지 못하는 블록이 있어 빠집니다: {skippedTypes.join(", ")}</li>
          )}
        </ConvertNotice>
      ),
      okText: "전환",
      cancelText: "취소",
    });
    if (!confirmed) return;

    setConvertedHtml(html);
    setFreeTextKey((key) => key + 1);
    setFreeTextMounted(true);
    setContentFormat("FREETEXT");
  };

  // 현재 URL 쿼리파라미터
  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    if (conferenceState?.conferenceInitialValues) {
      conferenceForm.setFieldsValue(conferenceState.conferenceInitialValues);
    }
  }, []);

  const handleSave = async (content: string | null, contentEn: string | null = null) => {
    // 저장 시점에 선택된 방식의 본문만 저장한다
    const options: PostContentOptions = {
      contentFormat,
      contentEn: isFreeText && englishEnabled ? contentEn : null,
    };
    try {
      if (isConferenceMode && conferenceState?.conferenceEditMode) {
        // Conference 수정
        const conferenceValues = conferenceForm.getFieldsValue();
        await postUpdatePostTable({
          postId: conferenceState.postData!.id,
          age: conferenceValues.meetAge,
          diagnosis: conferenceValues.meetDiagnosis,
          gender: conferenceValues.meetGender,
          kppNumber: conferenceValues.meetKppNumber,
          organ: conferenceValues.meetOrgan,
          organization: conferenceValues.meetOrganization,
        });
        await putPost(conferenceState.postData!.id, title, content, options);
        setCurrentPost(await getPostData(conferenceState.postData!.id));

        // 쿼리파라미터 구성
        const params = new URLSearchParams();
        params.set('soa', 'true');
        params.set('from', 'conference');
        params.set('title', title || '제목 없음');
        params.set('kppNumber', conferenceValues.meetKppNumber || 'Unknown');
        const page = searchParams.get('page');
        if (page) params.set('page', page);

        navigate(`/post/${conferenceState.postData!.id}?${params.toString()}`, { state: { soaData: conferenceValues } });
      } else if (isConferenceMode) {
        // Conference 신규
        const conferenceValues = conferenceForm.getFieldsValue();
        const { data: newPostId } = await postPostTable({
          categoryId: conferenceState!.rootCategoryId,
          ...conferenceValues,
        });
        await putPost(newPostId, title, content, options);
        setCurrentPost(await getPostData(newPostId));

        // 쿼리파라미터 구성
        const params = new URLSearchParams();
        params.set('soa', 'true');
        params.set('from', 'conference');
        params.set('title', title || '제목 없음');
        params.set('kppNumber', conferenceValues.meetKppNumber || 'Unknown');
        const page = searchParams.get('page');
        if (page) params.set('page', page);

        navigate(`/post/${newPostId}?${params.toString()}`, { state: { soaData: conferenceValues } });
      } else if (postState) {
        // 일반 수정
        await putPost(currentPost.id, title, content, options);
        setCurrentPost(await getPostData(currentPost.id));

        // 쿼리파라미터 구성 (breadcrumb용)
        const params = new URLSearchParams();
        params.set('title', title || '제목 없음');

        const from = searchParams.get('from');
        if (from) {
          params.set('from', from);

          // from에 따라 추가 파라미터 전달
          if (from === 'portal') {
            const category = searchParams.get('category');
            const categoryName = searchParams.get('categoryName');
            if (category) params.set('category', category);
            if (categoryName) params.set('categoryName', categoryName);
          } else if (from === 'dedicated') {
            const board = searchParams.get('board');
            const category = searchParams.get('category');
            const categoryName = searchParams.get('categoryName');
            if (board) params.set('board', board);
            if (category) params.set('category', category);
            if (categoryName) params.set('categoryName', categoryName);
          }
        }

        navigate(`/post/${currentPost.id}?${params.toString()}`);
      } else {
        // 일반 신규
        await postPost(currentPost.id, title, content, options);
        setCurrentPost(await getPostData(currentPost.id));

        // 쿼리파라미터 구성 (breadcrumb용)
        const params = new URLSearchParams();
        params.set('title', title || '제목 없음');

        const from = searchParams.get('from');
        if (from) {
          params.set('from', from);

          // from에 따라 추가 파라미터 전달
          if (from === 'portal') {
            const category = searchParams.get('category');
            const categoryName = searchParams.get('categoryName');
            if (category) params.set('category', category);
            if (categoryName) params.set('categoryName', categoryName);
          } else if (from === 'dedicated') {
            const board = searchParams.get('board');
            const category = searchParams.get('category');
            const categoryName = searchParams.get('categoryName');
            if (board) params.set('board', board);
            if (category) params.set('category', category);
            if (categoryName) params.set('categoryName', categoryName);
          }
        }

        navigate(`/post/${currentPost.id}?${params.toString()}`);
      }
    } catch (error) {
      console.error("Saving failed: ", error);
    }
  };

  return (
    <Layout.Content style={{ padding: "0 1rem" }}>
      <div style={{ textAlign: "center", marginBottom: "1rem", paddingTop: "1rem" }}>
        <Input
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          defaultValue={editPostData?.title}
          variant="borderless"
          size="large"
          style={{ maxWidth: "650px", borderBottom: "1px solid lightgrey" }}
        />
      </div>
      {isConferenceMode && (
        <ConferenceFormWrapper>
          <StyledCollapse
            defaultActiveKey={['1']}
            items={[
              {
                key: '1',
                label: 'Case Conference',
                children: (
                  <Form
                    form={conferenceForm}
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 16 }}
                  >
                    <Form.Item name="meetKppNumber" label="KPP Number">
                      <Input placeholder="Enter KPP number" />
                    </Form.Item>
                    <Form.Item name="meetGender" label="Gender">
                      <Input placeholder="Enter gender" />
                    </Form.Item>
                    <Form.Item name="meetAge" label="Age">
                      <Input placeholder="Enter age" />
                    </Form.Item>
                    <Form.Item name="meetOrgan" label="Organ">
                      <Input placeholder="Enter organ" />
                    </Form.Item>
                    <Form.Item name="meetDiagnosis" label="Diagnosis">
                      <Input.TextArea placeholder="Enter diagnosis" rows={3} />
                    </Form.Item>
                    <Form.Item name="meetOrganization" label="Organization">
                      <Input placeholder="Enter organization" />
                    </Form.Item>
                  </Form>
                ),
              },
            ]}
          />
        </ConferenceFormWrapper>
      )}
      {modalContextHolder}
      <FormatBar>
        {canConvert && !isFreeText && (
          <Button size="small" onClick={convertToFreeText}>
            {convertedHtml === null ? "FreeText로 전환" : "FreeText로 다시 전환"}
          </Button>
        )}
        <Tooltip
          title={
            switchLocked
              ? canConvert
                ? "이미 작성된 블록 글은 \"FreeText로 전환\"으로 바꿀 수 있습니다."
                : "이미 작성된 글은 입력 방식을 바꿀 수 없습니다."
              : undefined
          }
        >
          <label>
            <Switch size="small" checked={isFreeText} disabled={switchLocked} onChange={changeFreeText} />
            FreeText로 입력하기
          </label>
        </Tooltip>
        {isFreeText && (
          <Tooltip title="끄고 저장하면 영문 버전은 저장되지 않습니다.">
            <label>
              <Switch size="small" checked={englishEnabled} onChange={setEnglishEnabled} />
              영문 버전 입력하기
            </label>
          </Tooltip>
        )}
      </FormatBar>
      <div style={{ display: isFreeText ? "none" : undefined }}>
        <Editor
          postId={postId}
          initialContent={savedFormat === "BLOCK" ? editPostData?.content : undefined}
          onSave={(content) => handleSave(content)}
          instanceRef={blockEditorRef}
        />
      </div>
      {freeTextMounted && (
        <div style={{ display: isFreeText ? undefined : "none" }}>
          <FreeTextPostForm
            key={freeTextKey}
            postId={postId}
            initialHtml={savedFormat === "FREETEXT" ? editPostData?.content : convertedHtml}
            initialHtmlEn={savedFormat === "FREETEXT" ? editPostData?.contentEn : undefined}
            englishEnabled={englishEnabled}
            onSave={handleSave}
          />
        </div>
      )}
    </Layout.Content>
  );
};

// 저장된 본문에 내용이 있는지 (블록: 블록이 하나라도 있음 / FreeText: 글자, 이미지, 표가 있음)
const hasSavedContent = (format: ContentFormat, content?: string | null): boolean => {
  if (!content) return false;
  if (format === "FREETEXT") return hasFreeTextContent(content);
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed?.blocks) && parsed.blocks.length > 0;
  } catch {
    return content.trim().length > 0;
  }
};

export default Write;

const FormatBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 1.25rem;
  max-width: 60rem;
  margin: 0 auto 0.5rem;
  padding: 0 0.25rem;
  font-size: 0.9rem;
  color: #595959;

  label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
`;

const ConvertNotice = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1.1rem;
  color: #595959;

  li + li {
    margin-top: 0.35rem;
  }

  .warning {
    color: #d4380d;
  }
`;

const ConferenceFormWrapper = styled.div`
  max-width: 60rem;
  margin: 0 auto;
  padding: 1rem;
`;

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border: none;

  .ant-collapse-item {
    border-bottom: 1px solid #d9d9d9;
  }

  .ant-collapse-header {
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    color: #888 !important;
    text-transform: uppercase;
    padding: 9px 10px 12px !important;
  }

  .ant-collapse-content {
    border-top: none;
    background: transparent;
  }

  .ant-collapse-content-box {
    padding: 1rem 0;
  }
`;
